import { query } from "./strapi";

/**
 * Fetch cart groups by cartId
 * Requires both sessionId (via cookies) and cartId (from parameter)
 */
export async function findGroupsByCartId(cartId: string) {
  if (!cartId) {
    throw new Error("cartId is required");
  }

  try {
    console.log(`🔍 Fetching groups for cartId: ${cartId}`);

    const response = await query(`cart-groups/${cartId}`, {
      method: "GET",
      credentials: "include", // Include cookies for sessionId
    });

    console.log(`✅ Groups fetched: ${response?.groups?.length || 0}`);
    console.log("Response findGroupsByCartId:", JSON.stringify(response));
    return response;
  } catch (error) {
    console.error("❌ Error fetching groups:", error);
    throw error;
  }
}

/**
 * Update group properties (delivery method, date, etc.)
 * Also triggers delivery fee recalculation when method changes
 * Requires both sessionId (via cookies) and proper authorization
 */
export async function updateGroup(
  groupId: string,
  updates: Record<string, any>
) {
  const requestId = `grp-${Date.now()}`;
  console.log(`🔍 [${requestId}] updateGroup STARTED - groupId=${groupId}`);

  if (!groupId) {
    throw new Error("groupId is required");
  }

  try {
    console.log(`🔍 [${requestId}] Update params: ${JSON.stringify(updates)}`);

    console.log(`🔄 [CART-GROUP-API] Updating group ${groupId} with:`, updates);

    // Log specifically if destinationZoneId was sent to help debug
    if (updates.destinationZoneId) {
      console.log(
        `🌍 [CART-GROUP-API] Sending destination zone ID: ${updates.destinationZoneId}`
      );
    }

    // If changing delivery method or destination, add a flag to trigger fee recalculation
    if (updates.deliveryMethod || updates.destinationZoneId) {
      updates.recalculateFee = true;
      console.log(
        `🔄 [CART-GROUP-API] Setting recalculateFee=true due to ${
          updates.deliveryMethod ? "deliveryMethod" : "destinationZoneId"
        } change`
      );
    }

    // Explicitly log if we're requesting weight recalculation
    if (updates.recalculateWeight) {
      console.log(
        `⚖️ [CART-GROUP-API] Requesting weight recalculation for group ${groupId}`
      );
    }

    // Log if we're forcing an update
    if (updates.forceUpdate) {
      console.log(
        `🔄 [CART-GROUP-API] Force update requested for group ${groupId}`
      );
    }

    const response = await query(`cart-groups/update/${groupId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies for sessionId
      body: JSON.stringify(updates),
    });

    console.log(`✅ [${requestId}] updateGroup SUCCESS - response received`);

    console.log(`✅ [CART-GROUP-API] Group ${groupId} updated successfully:`, {
      groupId: response?.group?.id,
      deliveryFee: response?.group?.deliveryFee,
      totalWeight: response?.group?.totalWeight,
      subTotal: response?.group?.subTotal,
      groupTotal: response?.group?.groupTotal,
      cartVersion: response?.cartVersion,
    });

    // Enhanced logging specifically for delivery fee updates
    if (response?.group?.deliveryFee !== undefined) {
      console.log(`💰 [CART-GROUP-API] Updated delivery fee details:`, {
        groupId,
        fee: response.group.deliveryFee,
        weight: response.group.totalWeight,
        feeType: typeof response.group.deliveryFee,
        parsedFee: Number(response.group.deliveryFee),
        timestamp: new Date().toISOString(),
      });
    }

    // Log cart version update for tracking
    if (response?.cartVersion) {
      console.log(
        `🔢 [CART-GROUP-API] Cart version updated to: ${response.cartVersion}`
      );
    }

    return response;
  } catch (error) {
    console.error(`❌ [${requestId}] Error updating group:`, error);
    throw error;
  }
}

/**
 * Update group delivery status (restricted to authenticated users)
 */
export async function updateGroupStatus(
  groupId: string,
  groupDeliveryStatus: string
) {
  if (!groupId) {
    throw new Error("groupId is required");
  }

  try {
    console.log(
      `🔄 Updating group ${groupId} status to: ${groupDeliveryStatus}`
    );

    const response = await query(`cart-groups/${groupId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`, // Authentication token
      },
      credentials: "include", // Include cookies for sessionId
      body: JSON.stringify({ groupDeliveryStatus }),
    });

    console.log("✅ Group status updated successfully");
    return response;
  } catch (error) {
    console.error("❌ Error updating group status:", error);
    throw error;
  }
}

/**
 * Combined validate and reserve stock API
 * Validates stock availability and reserves it in a single request
 */
export async function validateAndReserveStock(cartGroups: any[], reservationTime: number = 15) {
  try {
    console.log("🔍 Validating and reserving stock for checkout");

    const response = await query(`cart/checkout/validate-and-reserve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // Include cookies for sessionId
      body: JSON.stringify({ cartGroups, reservationTime }),
    });

    console.log(`✅ Stock validation and reservation complete: ${response?.isValid ? "Valid" : "Invalid"}`);
    
    // If stock was successfully reserved, log the reservation expiry
    if (response?.isValid && response?.isReserved && response?.reservationExpiry) {
      console.log(`🕒 Stock reserved until: ${response.reservationExpiry}`);
    }
    
    return response;
  } catch (error) {
    console.error("❌ Error validating and reserving stock:", error);
    throw error;
  }
}

/**
 * Release stock reservation
 */
export async function releaseStock(
  cartGroupIds: string[] | null = null,
  releaseAll: boolean = false
) {
  try {
    console.log(
      `🔄 Releasing stock: ${
        releaseAll ? "all expired" : cartGroupIds?.length + " groups"
      }`
    );

    const response = await query(`cart/checkout/release-stock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // Include cookies for sessionId
      body: JSON.stringify({
        cartGroupIds,
        releaseAll,
      }),
    });

    console.log("✅ Stock released successfully");
    return response;
  } catch (error) {
    console.error("❌ Error releasing stock:", error);
    throw error;
  }
}

/**
 * Validate cart stock asynchronously
 * Used by the Cart page to highlight out-of-stock items without blocking UI rendering
 */

// DEPRECATED - These methods are no longer needed with our new architecture
/*
export async function addGroupToCart() { ... }
export async function removeGroupFromCart() { ... }
*/

/**
 * Get cart groups for checkout
 * Uses cartId from localStorage to fetch cart groups
 */
export async function getGroupsForCheckout() {
  try {
    const cartId = localStorage.getItem("cartId");
    if (!cartId) {
      throw new Error("No cartId found in localStorage");
    }

    console.log(`🔍 Fetching groups for checkout using cartId: ${cartId}`);

    const response = await findGroupsByCartId(cartId);

    if (!response) {
      throw new Error("No response from findGroupsByCartId");
    }

    // Calculate totals
    const totalPrice =
      response.groups?.reduce(
        (sum: number, group: any) => sum + (group.subTotal || 0),
        0
      ) || 0;

    const totalDeliveryFee =
      response.groups?.reduce(
        (sum: number, group: any) => sum + (group.deliveryFee || 0),
        0
      ) || 0;

    const cartTotal = totalPrice + totalDeliveryFee;

    return {
      groups: response.groups || [],
      totalPrice,
      totalDeliveryFee,
      cartTotal,
    };
  } catch (error) {
    console.error("❌ Error fetching groups for checkout:", error);
    throw error;
  }
}
